/**
 * Use Case - ProcessDepositUseCase
 *
 * Orchestre le processus complet de versement d'espèces
 *
 * RÈGLES MÉTIER APPLIQUÉES:
 * - Compte actif requis
 * - Transaction immutable
 * - Audit trail
 */

import { ITransactionRepository } from '../../ports/output/ITransactionRepository';
import { IAccountRepository } from '../../ports/output/IAccountRepository';
import { ICashRegisterRepository } from '../../ports/output/ICashRegisterRepository';
import { IAuditLogger } from '../../ports/output/IAuditLogger';
import { Account } from '../../../domain/entities/Account';
import { Transaction, TransactionType } from '../../../domain/entities/Transaction';
import { Money } from '../../../domain/value-objects/Money';
import { AccountNumber } from '../../../domain/value-objects/AccountNumber';
import { Result } from '../../../shared/types/Result';
import { DepositRequestDTO } from '../../dto/DepositRequestDTO';
import { RecuOperationBackend, TransactionResponseDTO } from '../../dto/TransactionResponseDTO';
import { IDepositGateway } from '../../ports/output/IDepositGateway';
import {
  InvalidAmountError,
  EntityNotFoundError,
  AccountBlockedError,
} from '../../../domain/errors/DomainErrors';

export class ProcessDepositUseCase {
  constructor(
    private readonly transactionRepo: ITransactionRepository,
    private readonly accountRepo: IAccountRepository,
    private readonly cashRegisterRepo: ICashRegisterRepository,
    private readonly auditLogger: IAuditLogger,
    private readonly depositGateway: IDepositGateway | null
  ) {}

  /**
   * Exécute le versement.
   * @param input - données du versement
   * @param resolvedAccount - compte déjà résolu via CBS (évite un re-fetch)
   */
  async execute(
    input: DepositRequestDTO,
    resolvedAccount?: Account
  ): Promise<Result<TransactionResponseDTO, Error>> {
    try {
      // 1. Utiliser le compte pré-résolu ou le chercher dans le repository
      const accountNumber = AccountNumber.create(input.accountNumber);
      let account: Account | null = resolvedAccount ?? null;

      if (!account) {
        account = await this.accountRepo.findByNumber(accountNumber);
      }

      if (!account) {
        return Result.fail(
          new EntityNotFoundError('Account', input.accountNumber)
        );
      }

      // 2. Vérifier que le compte peut recevoir un versement
      if (!account.canReceiveDeposit()) {
        return Result.fail(
          new AccountBlockedError(input.accountNumber, account.status)
        );
      }

      // 3. Calculer le montant total
      const totalAmount = this.calculateTotal(input.denomination);
      if (totalAmount <= 0) {
        return Result.fail(
          new InvalidAmountError(totalAmount)
        );
      }

      const money = Money.create(totalAmount, 'XOF');
      const denominationMap = new Map(
        Object.entries(input.denomination).map(([k, v]) => [Number(k), v])
      );

      // 3b. Validation & envoi au CBS via backend CardLife (si configuré)
      let receiptPdfBase64: string | undefined;
      let externalReference: string | undefined;
      let recu: RecuOperationBackend | undefined;
      if (this.depositGateway) {
        const gatewayResult = await this.depositGateway.submitDeposit({
          ...input,
          amount: totalAmount,
        });
        receiptPdfBase64 = gatewayResult.receiptPdfBase64;
        externalReference = gatewayResult.externalReference;
        recu = gatewayResult.recu;
      }

      // 4. Déterminer les informations du déposant
      const clientId = input.depositorInfo.isThirdParty
        ? input.depositorInfo.thirdParty?.idNumber || ''
        : input.depositorInfo.clientId || account.primaryHolder.id;

      const clientName = input.depositorInfo.isThirdParty
        ? input.depositorInfo.thirdParty?.name || ''
        : input.depositorInfo.clientName || account.primaryHolder.name;

      // 5. Créer la transaction
      const transaction = Transaction.create({
        type: TransactionType.DEPOSIT,
        amount: money,
        accountNumber: accountNumber,
        clientId: clientId,
        clientName: clientName,
        cashierId: input.cashierId,
        cashierName: input.cashierName,
        denomination: denominationMap,
        notes: input.notes,
        depositorIsThirdParty: input.depositorInfo.isThirdParty,
        thirdPartyInfo: input.depositorInfo.thirdParty,
      });

      // 6. Persister
      await this.transactionRepo.save(transaction);

      // 7. Mettre à jour la caisse (ajouter les billets)
      const cashRegister = await this.cashRegisterRepo.get();
      cashRegister.receive(denominationMap);
      await this.cashRegisterRepo.save(cashRegister);

      // 8. Logger l'audit
      await this.auditLogger.logDeposit({
        transactionId: transaction.id.value,
        accountNumber: input.accountNumber,
        amount: totalAmount,
        depositorInfo: input.depositorInfo,
        timestamp: transaction.createdAt,
        cashierId: input.cashierId,
        cashierName: input.cashierName,
      });

      // 9. Construire la réponse
      // Le reçu présente le TITULAIRE du compte ; le déposant (lui-même ou un tiers) est indiqué à part.
      const tiers = input.depositorInfo.isThirdParty;
      const response: TransactionResponseDTO = {
        id: transaction.id.value,
        type: 'DEPOSIT',
        amount: recu?.montant ?? transaction.amount.value,
        currency: recu?.devise ?? 'XOF',
        accountNumber: recu?.numcpt ?? transaction.accountNumber.value,
        clientId: transaction.clientId,
        clientName: recu?.intituleCompte || account.primaryHolder.name || transaction.clientName,
        cashierId: transaction.cashierId,
        cashierName: transaction.cashierName,
        createdAt: recu?.dateOperation ?? transaction.createdAt.toISOString(),
        reference: recu?.reference ?? externalReference ?? transaction.reference,
        agence: recu?.agence ?? undefined,
        deposant: recu?.deposant ?? {
          tiers,
          nom: tiers ? input.depositorInfo.thirdParty?.name : account.primaryHolder.name,
          pieceType: tiers ? input.depositorInfo.thirdParty?.idType : undefined,
          pieceNumero: tiers ? input.depositorInfo.thirdParty?.idNumber : undefined,
          contact: tiers ? input.depositorInfo.thirdParty?.contact : undefined,
        },
        biotransacRef: transaction.biotransacRef,
        status: 'COMPLETED',
        notes: transaction.notes,
        denomination: input.denomination,
        receiptPdfBase64,
        externalReference,
      };

      return Result.ok(response);
    } catch (error) {
      console.error('[ProcessDepositUseCase] Erreur:', error);
      return Result.fail(
        error instanceof Error ? error : new Error('Erreur inconnue lors du versement')
      );
    }
  }

  private calculateTotal(denomination: Record<number, number>): number {
    return Object.entries(denomination).reduce(
      (total, [denom, count]) => total + Number(denom) * count,
      0
    );
  }
}
